import {
  Button,
  Divider,
  Flex,
  List,
  Popover,
  Table,
  TableColumnsType,
  Tag
} from 'antd'
import { useTheme } from 'antd-style'
import dayjs from 'dayjs'
import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { useStocksServiceGetInternalEntryListEndpoint } from 'src/api/queries'
import {
  InternalInventoryDocumentState,
  InventoryDocumentState,
  InvoicedDocumentResponse,
  Permissions
} from 'src/api/requests'
import { PageBreadcrumbs, Text } from 'src/components'
import { getStatus, SelectedItemsState } from 'src/features/stock'
import { useStyles } from 'src/features/stock/components/inputs/styles'
import { LocalInputsFilters } from 'src/features/stock-local/components/'
import { useTablePagination, useUser } from 'src/hooks'
import {
  dayMonthYearBackSlash,
  dayStringMonthYearHourMinutesFormat
} from 'src/libs'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'

const VISIBLE_TAG_COUNT = 2

const MapValuesToIds = (data: SelectedItemsState) => {
  const { userData, hasPermission } = useUser()

  const isStockAdmin = hasPermission(
    Permissions.STOCKS_INTERNAL_MENU_ACCESS_ISSUANCE
  )

  return {
    roleIds: !isStockAdmin
      ? [userData?.currentRoleId ?? '']
      : data?.roleIds?.values,
    fromDate: data?.inDate?.values?.[0],
    toDate: data?.inDate?.values?.[1],
    searchTerm: data?.search?.values?.[0],
    states: data?.states?.values as InternalInventoryDocumentState[]
  }
}

export const LocalStockInputsPage = () => {
  const { hasPermission } = useUser()

  const [selectedItems, setSelectedItems] = useState<SelectedItemsState>({})

  const { styles } = useStyles()
  const palette = useTheme()
  const navigate = useNavigate()
  const { t } = useLocales()

  const pagination = useTablePagination()

  const isStockAdmin = hasPermission(
    Permissions.STOCKS_INTERNAL_STOCK_CREATE_ENTRIES
  )

  const queryParams = {
    pageNumber: pagination.current,
    pageSize: pagination.pageSize,
    ...MapValuesToIds(selectedItems)
  }

  const { data, isLoading } =
    useStocksServiceGetInternalEntryListEndpoint(queryParams)

  const columns = useMemo(
    (): TableColumnsType<InvoicedDocumentResponse> => [
      {
        key: 'documentDate',
        title: t('stock_applications.entry_date'),
        render: ({ documentDate }) =>
          dayjs(documentDate).format(dayMonthYearBackSlash)
      },
      {
        key: 'supplier',
        title: t('stock_administration.juridic_person'),
        dataIndex: ['role', 'name']
      },
      {
        key: 'products',
        title: t('stock_applications.consumables'),
        render: (_, { items }) => {
          const extraCount = (items?.length ?? 0) - VISIBLE_TAG_COUNT

          return (
            <Flex>
              {items?.length ? (
                <>
                  <List
                    itemLayout="horizontal"
                    dataSource={items}
                    renderItem={(item, index) =>
                      index < VISIBLE_TAG_COUNT && (
                        <Tag
                          key={item?.id}
                        >{`${item?.name} (${item?.quantity})`}</Tag>
                      )
                    }
                  />
                  <Popover
                    content={
                      <Flex vertical gap={4}>
                        {items?.slice(VISIBLE_TAG_COUNT).map((item) => (
                          <Tag
                            key={item?.id}
                          >{`${item?.name} (${item?.quantity})`}</Tag>
                        ))}
                      </Flex>
                    }
                  >
                    {extraCount > 0 && (
                      <Tag className="pointer">+{extraCount}</Tag>
                    )}
                  </Popover>
                </>
              ) : (
                '-'
              )}
            </Flex>
          )
        }
      },
      {
        key: 'state',
        title: t('form.status'),
        render: ({ state }) => {
          return (
            <Tag color={getStatus(t, state).color} key={state}>
              {getStatus(t, state).text}
            </Tag>
          )
        }
      },
      {
        key: 'details',
        align: 'right',
        render: ({ id }) => {
          return (
            <Link to={paths.stockLocal.inputs.details(id)}>
              {t('common.details')}
            </Link>
          )
        }
      }
    ],
    [t]
  )

  return (
    <PageBreadcrumbs
      title={t('stock.inputs')}
      items={[
        { title: t('menu.consumable_medicaments') },
        { title: t('stock.inputs') }
      ]}
    >
      <Flex vertical gap={16}>
        <Flex gap={16} className="full-width">
          <LocalInputsFilters
            selectedItems={selectedItems}
            setSelectedItems={setSelectedItems}
          />
        </Flex>

        {!isStockAdmin &&
          data?.data?.map(
            (item, index) =>
              item.state === InventoryDocumentState.NEW && (
                <Flex
                  key={index}
                  vertical
                  gap={8}
                  className={styles.inputNewCard}
                >
                  <Flex gap={8} justify="space-between">
                    <Flex gap={8} vertical>
                      <>
                        <Text color={palette.grey.grey1}>
                          {dayjs().format(dayStringMonthYearHourMinutesFormat)}
                        </Text>

                        <Text weight={600} size="xl">
                          {t('stock.received_new_stock')}
                        </Text>
                        <Flex gap={4}>
                          <Text weight={400}>
                            {t('stock.medicines_includes')}:
                          </Text>
                          <List
                            grid={{ gutter: 4 }}
                            itemLayout="horizontal"
                            dataSource={item?.items}
                            renderItem={(item, index) =>
                              index < VISIBLE_TAG_COUNT && (
                                <Tag
                                  key={item?.id}
                                >{`${item?.name} (${item?.quantity})`}</Tag>
                              )
                            }
                          />
                          <Popover
                            content={
                              <Flex vertical gap={4}>
                                {item?.items
                                  ?.slice(VISIBLE_TAG_COUNT)
                                  .map((item) => (
                                    <Tag
                                      key={item?.id}
                                    >{`${item?.name} (${item?.quantity})`}</Tag>
                                  ))}
                              </Flex>
                            }
                          >
                            {(item?.items?.length ?? 0) - VISIBLE_TAG_COUNT >
                              0 && (
                              <Tag className="pointer">
                                +
                                {(item?.items?.length ?? 0) - VISIBLE_TAG_COUNT}
                              </Tag>
                            )}
                          </Popover>
                        </Flex>
                      </>
                    </Flex>
                    <Flex
                      align="center"
                      onClick={() =>
                        navigate(
                          paths.stockLocal.inputs.details(String(item?.id))
                        )
                      }
                    >
                      <Button type="primary">{t('common.see_details')}</Button>
                    </Flex>
                  </Flex>
                </Flex>
              )
          )}

        <Divider />

        <Table
          rowKey="id"
          columns={columns}
          loading={isLoading}
          dataSource={data?.data}
          scroll={{ x: 'max-content' }}
          pagination={{ ...pagination, total: data?.totalCount }}
        />
      </Flex>
    </PageBreadcrumbs>
  )
}
